import { useEffect, useMemo, useState } from "react";
import ResourceList from "@/components/ResourceList";
import ReportDocumentView from "@/components/ReportDocumentView";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ADMIN_ROLES = ["admin", "control", "planner", "superintendent", "supervisor", "owner"];
const SUP_ROLES = ["supervisor", "superintendent", "owner"];

// Hoisted so ResourceList/DetailDrawer see the same component identity across
// renders (avoids the react/no-unstable-nested-components lint rule and any
// unwanted state-loss when the drawer re-renders).
const renderHazardDoc = (hz) => <ReportDocumentView type="hazard" item={hz} />;

export default function Hazards() {
  const { user } = useAuth();
  // Prefetch users so we can resolve supervisor id → name for meta display and
  // to sort "assigned to me" first.
  const [users, setUsers] = useState([]);
  useEffect(() => {
    let cancelled = false;
    api.get("/admin/users")
      .then((r) => {
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : (r.data?.items || r.data?.results || []);
        setUsers(arr);
      })
      .catch(() => setUsers([]));
    return () => { cancelled = true; };
  }, []);

  const userById = useMemo(() => {
    const m = {};
    users.forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const nameOfSupervisor = (id) => {
    if (!id) return null;
    const u = userById[id];
    return u ? `${u.name}${u.crew_name ? ` · ${u.crew_name}` : ""}` : id.slice(0, 8);
  };

  return (
    <ResourceList
      endpoint="/hazards/pending"
      title="Site Hazards"
      subtitle="Safety · Awaiting Sign-off"
      resourceName="Hazard"
      documentStyle
      extraDetail={renderHazardDoc}
      itemLabel={(it) => it.description || "Hazard"}
      itemMeta={(it) => [
        { label: "Location", value: it.location },
        { label: "Risk", value: it.risk_level },
        { label: "Assigned Supervisor", value: nameOfSupervisor(it.assigned_supervisor_id) },
        { label: "Reported By", value: it.tradesman_name || it.created_by_name },
        { label: "Created", value: fmtDate(it.created_at || it.reported_at || it.ts) },
      ]}
      statusOf={(it) => {
        if (user?.id && it.assigned_supervisor_id === user.id) return "assigned to me";
        return String(it.status || "pending").toLowerCase();
      }}
      priorityOf={(it) => it.risk_level || it.severity || it.priority}
      searchFields={["description", "location", "reported_to", "corrective_action", "risk_level"]}
      testid="hazards-page"
      emptyLabel="No hazards awaiting sign-off."
      // Once a hazard is signed off (status: "actioned"), it disappears from
      // this list. The record persists in the shift-summary hazards trail so
      // supervisors can still review it in end-of-shift reports.
      filterItems={(it) => {
        const s = String(it.status || "pending").toLowerCase();
        return s !== "actioned" && s !== "closed";
      }}
      // Sort assigned-to-me first, then everyone else by newest.
      sortItems={(a, b) => {
        const rank = (it) => {
          const mine = user?.id && it.assigned_supervisor_id === user.id;
          return mine ? 0 : 1;
        };
        const ra = rank(a); const rb = rank(b);
        if (ra !== rb) return ra - rb;
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      }}
      signOffConfig={{
        endpoint: (it) => `/hazards/${it.id || it._id}/action`,
        label: "Sign to Close",
        verb: "sign",
        requireSignature: true,
        submitLabel: "Sign off hazard",
        noteLabel: "Mitigation notes (optional)",
        notePlaceholder: "How was this resolved?",
        // Hide the sticky prompt once the hazard has been actioned/closed.
        alreadySigned: (it) => {
          const s = String(it.status || "").toLowerCase();
          return s === "actioned" || s === "closed";
        },
        // Use "actioned" (not "closed") — the API DELETES on "closed", which
        // breaks the Shift Summary trail. "actioned" preserves the record.
        payload: ({ signature, notes }) => ({
          action: "actioned",
          notes,
          signature_svg: signature,
          supervisor_sig: signature,
          supervisor_name: user?.name || user?.username || "Supervisor",
          signed_at: new Date().toISOString(),
        }),
      }}
      createConfig={{
        singular: "Hazard",
        title: "Report New Hazard",
        endpoint: "/hazards",
        roles: ADMIN_ROLES,
        fields: [
          { name: "description", label: "Risk Description *", type: "textarea", rows: 3, required: true, placeholder: "Hydraulic hose leaking near operator platform" },
          { name: "location", label: "Location *", required: true, placeholder: "e.g. Line 2, Bay 3" },
          {
            name: "assigned_supervisor_id",
            label: "Assign to Supervisor *",
            required: true,
            type: "select-async",
            optionsEndpoint: "/admin/users",
            filter: (u) => SUP_ROLES.includes(String(u.role || "").toLowerCase()),
            // Sort: same crew as reporter first, then everyone else alphabetically.
            sort: (a, b) => {
              const myCrew = user?.crew_id;
              const aMatch = myCrew && a.crew_id === myCrew ? 0 : 1;
              const bMatch = myCrew && b.crew_id === myCrew ? 0 : 1;
              if (aMatch !== bMatch) return aMatch - bMatch;
              return String(a.name || "").localeCompare(String(b.name || ""));
            },
            optionLabel: (u) => `${u.name} · ${(u.role || u.trade || "").toString().toUpperCase()}${u.crew_name ? ` · ${u.crew_name}` : ""}`,
            hint: "This supervisor will be notified and can sign off the hazard.",
          },
          { name: "risk_level", label: "Risk Assessment Level *", required: true, type: "select", options: ["low", "medium", "high", "critical"] },
          { name: "corrective_action", label: "Mitigation Strategy", type: "textarea", rows: 2 },
          { name: "shift", label: "Shift", type: "select", options: ["day", "night", "morning", "afternoon", "swing"] },
          { name: "crew", label: "Crew", placeholder: user?.crew_name || "A Crew / B Crew / …" },
          { name: "photos", label: "Photos", type: "photos", max: 6 },
        ],
      }}
    />
  );
}

function fmtDate(v) {
  if (!v) return null;
  try { return new Date(v).toLocaleString(); } catch { return String(v); }
}
